import { FC, useCallback, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
// T16: switch al rebuild 3d_new (barrel pubblico). La vecchia web/src/3d/
// resta intatta ma non è più usata dal viewer (rollback deciso in review).

import type { BeamVisibility, CalculationLegend, CameraPreset, LockSceneComponentsState, ViewPresetRequest } from "../../3d";
import { beamDisplayState, LockScene } from "../../3d";
import styles from "../../LockCalculator.module.css";
import type { LockCalculatorFormValues } from "../../model/form";
import { useLockSceneInput, type ConnectorCodes, type FastenerSpecs, type TopDistanceBase } from "../../model/toLockSceneInput";
import { isDebugToolsEnabled } from "../../../../../utils/environment";

export type LockCalculator3DViewerProps = {
  className?: string;
  /** Real fastener lengths from the loaded calculation's Configuration, so the
   * 3D fasteners are sized to the actually selected fasteners. */
  fastenerSpecs?: FastenerSpecs;
  /** Catalog code of each connector pair, from the loaded Configuration — shown in the 3D legend. */
  connectorCodes?: ConnectorCodes;
  /** Catalog code of the wider header lock, when one is chosen — shown in the 3D legend. */
  headerLockCode?: string | null;
  /**
   * EC5 service class (28-09-2026) — colors the 3D preview background and
   * shows in its legend. `label` is the already-translated field name (this
   * component has no i18n of its own), just the class number is shown — no
   * description in the legend (too noisy, dropped after user feedback).*/
  serviceClass?: { classNumber: number; label: string } | null;
  /** Righe di legenda sul preforo di header e joist, gia' tradotte (decide il backend). */
  preDrillingLines?: string[];
  /** Legenda sulle impostazioni del calcolo (norma, coefficienti, materiale…), gia' tradotta; vedi `buildCalculationLegend`. */
  calculationLegend?: CalculationLegend;
  /**
   * Livelli di installazione del lockstop scelto, dalla Configuration
   * caricata (28-09-2026) — vedi doc-comment su `LockSceneProps.lockstopInstallationLevels`.*/
  lockstopInstallationLevels?: number | null;
  /** Codice a catalogo del lockstop scelto (es. "LOCKSTOP35") — decide wide/narrow. */
  lockstopCode?: string | null;
  /** `lock.screw_edge_thickness` (mm) — vedi doc-comment su `LockSceneProps.screwEdgeThicknessMm`. */
  screwEdgeThicknessMm?: number | null;
  /** The chosen lock module's c_min (mm) — default for the connector's distance from the joist top. */
  topDistanceBase?: TopDistanceBase | null;
};

export const LockCalculator3DViewer: FC<LockCalculator3DViewerProps> = ({
  className,
  fastenerSpecs,
  connectorCodes,
  headerLockCode,
  serviceClass,
  preDrillingLines,
  calculationLegend,
  lockstopInstallationLevels,
  lockstopCode,
  screwEdgeThicknessMm,
  topDistanceBase,
}) => {
  // Keep geometric references visible while the lock/fastener placement is being calibrated.
  const debugToolsEnabled = isDebugToolsEnabled();
  const [showDebug, setShowDebug] = useState(false);
  const { control } = useFormContext<LockCalculatorFormValues>();
  const form = useWatch({ control }) as LockCalculatorFormValues | undefined;
  const input = useLockSceneInput(form, fastenerSpecs, connectorCodes, topDistanceBase, headerLockCode);

  // Camera preset: seq increments on every click so re-clicking the same
  // preset still triggers the controller inside Canvas.
  const [viewPreset, setViewPreset] = useState<ViewPresetRequest | null>(null);

  const requestPreset = useCallback((view: CameraPreset) => {
    setViewPreset((prev) => ({ view, seq: (prev?.seq ?? 0) + 1 }));
  }, []);

  // Visibilita' degli elementi di scena. Le travi hanno tre stati perche' il
  // caso d'uso e' guardare l'incastro: "ghost" e' il default storico.
  const [headerBeam, setHeaderBeam] = useState<BeamVisibility>("ghost");
  const [joistBeam, setJoistBeam] = useState<BeamVisibility>("ghost");
  const [showHeaderLock, setShowHeaderLock] = useState(true);
  const [showJoistLock, setShowJoistLock] = useState(true);
  const [showFasteners, setShowFasteners] = useState(true);
  const [showLockstop, setShowLockstop] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  // Quote tecniche: OFF di default per non affollare la vista al primo carico.
  const [showQuotes, setShowQuotes] = useState(false);
  // Frecce dei carichi applicati (28-09-2026, richiesta utente): OFF di
  // default come le quote, stesso motivo (overlay opzionale, non affollare
  // la prima vista).
  const [showForces, setShowForces] = useState(false);
  // Vista esplosa (richiesta utente 24-09-2026): OFF di default, un toggle
  // nel menu View invece di uno slider — l'esploso e' "a stadi" (travi →
  // connettore → viti standard → viti inclinate, vedi EXPLODE_DEFAULTS in
  // config/defaults.ts), non un'animazione continua.
  const [exploded, setExploded] = useState(false);

  const componentsState = useMemo<LockSceneComponentsState>(
    () => ({
      headerBeam: beamDisplayState(headerBeam),
      joistBeam: beamDisplayState(joistBeam),
      headerLock: { visible: showHeaderLock },
      joistLock: { visible: showJoistLock },
      fasteners: { visible: showFasteners },
      lockstop: { visible: showLockstop },
      grid: { visible: showGrid },
      quotes: { visible: showQuotes },
      forces: { visible: showForces },
      explodeFactor: exploded ? 1 : 0,
    }),
    [
      headerBeam,
      joistBeam,
      showHeaderLock,
      showJoistLock,
      showFasteners,
      showLockstop,
      showGrid,
      showQuotes,
      showForces,
      exploded,
    ],
  );

  return (
    <div className={`${styles.viewer3d} ${className ?? ""}`}>
      {/* ── 3D Canvas ── */}
      <div className={styles.viewer3dContainer}>
        {input && (
          <LockScene
            input={input}
            showDebug={debugToolsEnabled && showDebug}
            viewPreset={viewPreset}
            componentsState={componentsState}
            serviceClass={serviceClass}
            preDrillingLines={preDrillingLines}
            calculationLegend={calculationLegend}
            loads={form?.loads}
            lockstopInstallationLevels={lockstopInstallationLevels}
            lockstopCode={lockstopCode}
            screwEdgeThicknessMm={screwEdgeThicknessMm}
          />
        )}
      </div>

      {/* ── Bottom toolbar ── */}
      <div className={styles.viewer3dToolbar}>
        <div className={styles.viewer3dControls}>
          {/* View menu */}
          <div className={styles.viewer3dViewMenu}>
            <button type="button" className={styles.viewer3dBtnText} title="View presets">
              View ▾
            </button>
            <div className={styles.viewer3dViewDropdown}>
              <button type="button" onClick={() => requestPreset("front")}>
                Front
              </button>
              <button type="button" onClick={() => requestPreset("top")}>
                Top
              </button>
              <button type="button" onClick={() => requestPreset("side")}>
                Side
              </button>
              <button type="button" onClick={() => requestPreset("perspective")}>
                Perspective
              </button>
              {/* Vista esplosa: toggle, non un preset one-shot — resta attiva
              cambiando vista, cosi' si puo' ispezionare l'esploso da ogni lato. */}
              <button
                type="button"
                aria-pressed={exploded}
                className={exploded ? styles.viewer3dBtnActive : undefined}
                onClick={() => setExploded((v) => !v)}
              >
                Exploded view
              </button>
            </div>
          </div>

          {/* Elements menu → show/hide scene elements */}
          <div className={styles.viewer3dViewMenu}>
            <button type="button" className={styles.viewer3dBtnText} title="Show or hide elements">
              Elements ▾
            </button>
            <div className={`${styles.viewer3dViewDropdown} ${styles.viewer3dElementsDropdown}`}>
              <BeamVisibilityRow label="Header beam" value={headerBeam} onChange={setHeaderBeam} />
              <BeamVisibilityRow label="Joist beam" value={joistBeam} onChange={setJoistBeam} />
              <ElementToggleRow label="Header lock" checked={showHeaderLock} onChange={setShowHeaderLock} />
              <ElementToggleRow label="Joist lock" checked={showJoistLock} onChange={setShowJoistLock} />
              <ElementToggleRow label="Fasteners" checked={showFasteners} onChange={setShowFasteners} />
              <ElementToggleRow label="Lockstop" checked={showLockstop} onChange={setShowLockstop} />
              <ElementToggleRow label="Grid" checked={showGrid} onChange={setShowGrid} />
              <ElementToggleRow label="Quotes" checked={showQuotes} onChange={setShowQuotes} />
              <ElementToggleRow label="Forces" checked={showForces} onChange={setShowForces} />
            </div>
          </div>

          {/* Reset view → back to default perspective */}
          <button
            type="button"
            className={styles.viewer3dBtn}
            title="Reset view"
            onClick={() => requestPreset("perspective")}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
          </button>

          {/* Toggle constraint debug helpers: staging and local work only, never production. */}
          {debugToolsEnabled && (
            <button
              type="button"
              className={`${styles.viewer3dBtn} ${showDebug ? styles.viewer3dBtnActive : ""}`}
              title="Toggle constraint debug"
              onClick={() => setShowDebug((v) => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </button>
          )}
        </div>

        {/* <span className={styles.viewer3dInfo}>
          LOCK‑T connection · <em>parametric 3D preview</em>
        </span> */}
      </div>
    </div>
  );
};

// ─── Righe del menu Elements ──────────────────────────────────

/** Elemento con due soli stati: c'e' o non c'e'. */
function ElementToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={styles.viewer3dElementRow}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

const BEAM_MODES: Array<{ mode: BeamVisibility; short: string; title: string }> = [
  { mode: "solid", short: "■", title: "Solid" },
  { mode: "ghost", short: "◧", title: "Semi-transparent" },
  { mode: "hidden", short: "□", title: "Hidden" },
];

/** Trave: tre stati invece di due, perche' semitrasparente e' il default e
 * serve poterci tornare dopo averla nascosta. */
function BeamVisibilityRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BeamVisibility;
  onChange: (next: BeamVisibility) => void;
}) {
  return (
    <div className={styles.viewer3dElementRow}>
      <span className={styles.viewer3dElementLabel}>{label}</span>
      <span className={styles.viewer3dBeamModes}>
        {BEAM_MODES.map((option) => (
          <button
            key={option.mode}
            type="button"
            title={option.title}
            aria-pressed={value === option.mode}
            className={`${styles.viewer3dBeamMode} ${value === option.mode ? styles.viewer3dBeamModeActive : ""}`}
            onClick={() => onChange(option.mode)}
          >
            {option.short}
          </button>
        ))}
      </span>
    </div>
  );
}
